import React from "react";
import { getLogoLink } from "../logic";
import { STRINGS } from "../strings";
import { Logo, LogoDivider, LogosWrapper } from "../HeaderV2.styles";
import { HeaderScrollState, type HeaderV2ImageConfig } from "../types";

type LogosProps<T> = {
  image: HeaderV2ImageConfig<T>;
  image2?: HeaderV2ImageConfig<T>;
  showImage2?: boolean;
  imageScrolled?: HeaderV2ImageConfig<T>;
  image2Scrolled?: HeaderV2ImageConfig<T>;
  showImageScrolled?: boolean;
  scrollState?: HeaderScrollState;
};

const hasSrc = <T,>(image: HeaderV2ImageConfig<T> | undefined): image is HeaderV2ImageConfig<T> =>
  !!image && typeof image === "object" && !!(image.props as { src?: string } | undefined)?.src;

const SingleLogo = <T,>({
  image,
  index,
  fieldPath,
}: {
  image: HeaderV2ImageConfig<T> | undefined;
  index: 1 | 2;
  fieldPath: string;
}) => {
  if (!hasSrc(image)) return null;
  const imgProps = image.props as { alt?: string };

  const element = image.render({
    ...image.props,
    className: "image",
    alt: imgProps.alt || STRINGS.logoFallbackAlt(index),
    "data-af-field": `${fieldPath}/props/src`,
  } as T);

  const link = getLogoLink({ redirect: image.redirect });

  return (
    <Logo>
      {link ? (
        <a href={link.href} target={link.target} aria-label={STRINGS.logoLinkLabel(index)}>
          {element}
        </a>
      ) : (
        element
      )}
    </Logo>
  );
};

export const Logos = <T,>({
  image,
  image2,
  showImage2,
  imageScrolled,
  image2Scrolled,
  showImageScrolled,
  scrollState = HeaderScrollState.TOP,
}: LogosProps<T>) => {
  const scrolledVersions = scrollState === HeaderScrollState.SCROLLED && showImageScrolled;
  const useFirstScrolled = scrolledVersions && hasSrc(imageScrolled);
  const useSecondScrolled = scrolledVersions && hasSrc(image2Scrolled);
  const first = (
    <SingleLogo
      image={useFirstScrolled ? imageScrolled : image}
      index={1}
      fieldPath={useFirstScrolled ? "imageScrolled" : "image"}
    />
  );
  const second = useSecondScrolled ? image2Scrolled : image2;
  const hasSecond = showImage2 && hasSrc(second);

  if (!hasSecond) return first;

  return (
    <LogosWrapper>
      {first}
      <LogoDivider aria-hidden="true" />
      <SingleLogo image={second} index={2} fieldPath={useSecondScrolled ? "image2Scrolled" : "image2"} />
    </LogosWrapper>
  );
};
